import React from 'react';
import { useFeatureFlagContext } from './FeatureFlagContext';
import type { FeatureFlagProps } from './types';

/**
 * Wrapper component for conditionally rendering content based on feature flags.
 *
 * Shows children only if the specified feature is enabled for the current environment.
 * Optionally renders fallback content when the feature is disabled.
 *
 * Future: Will support role-based access via requiredRole prop when security is implemented.
 *
 * @example
 * // Basic usage - show children if feature is enabled
 * <FeatureFlag name="new-dashboard">
 *   <NewDashboard />
 * </FeatureFlag>
 *
 * @example
 * // With fallback content when feature is disabled
 * <FeatureFlag name="new-dashboard" fallback={<OldDashboard />}>
 *   <NewDashboard />
 * </FeatureFlag>
 *
 * @example
 * // With future role requirement (not enforced yet)
 * <FeatureFlag name="admin-panel" requiredRole="admin">
 *   <AdminPanel />
 * </FeatureFlag>
 */
export const FeatureFlag: React.FC<FeatureFlagProps> = ({
    name,
    children,
    fallback = null,
    requiredRole,
}) => {
    const { isEnabled, hasRoleAccess } = useFeatureFlagContext();

    const featureEnabled = isEnabled(name);

    // Future: Role checking will be enforced here
    // Currently hasRoleAccess always returns true when no roles are defined
    const roleAllowed = hasRoleAccess(name, requiredRole);

    // Feature must be enabled AND (when implemented) role must be allowed
    if (featureEnabled && roleAllowed) {
        return <>{children}</>;
    }

    return <>{fallback}</>;
};

export default FeatureFlag;
